import {
  createTheme,
  assignConditionalTokens,
  assignTokens,
  toIntent,
} from '@sail/engine';
import type {Intent, Token} from '@sail/engine';
import type {ColorSpectrum} from '@sail/tokens/deprecated';
import {tokens, createColorStates} from '@sail/tokens/deprecated';
import {css} from '../../css';
import {DrawerConfig} from '../../components/drawer/DrawerConfig';
import {DialogConfig} from '../../components/dialog/DialogConfig';
import {TooltipConfig} from '../../components/TooltipConfig';
import {BackdropConfig} from '../../components/BackdropConfig';
import {PopoverConfig} from '../../components/popover/PopoverConfig';
import {CounterConfig} from '../../components/Counter';
import {LinkConfig} from '../../components/Link';
import {
  lightModeComponentOverrides,
  lightModeTokenOverrides,
} from '../lightMode/lightMode';

const {color} = tokens;

const darkModeColors = {
  blue: {
    0: '#03143d',
    25: '#051a4c',
    33: '#051c50',
    50: '#062059',
    100: '#092a70',
    200: '#0f3d99',
    300: '#1650bc',
    400: '#1c66d8',
    500: '#2280eb',
    600: '#2b9df6',
    700: '#4abcf9',
    800: '#79dbf9',
    900: '#b9f9fb',
    1000: '#f3fefe',
  },
  green: {
    0: '#101a05',
    25: '#152207',
    33: '#162408',
    50: '#192909',
    100: '#20360c',
    200: '#284d10',
    300: '#2d6312',
    400: '#317a14',
    500: '#369318',
    600: '#3eae20',
    700: '#4bca2e',
    800: '#5eea42',
    900: '#c2fdbd',
    1000: '#f4fff3',
  },
  orange: {
    0: '#330700',
    25: '#400a00',
    33: '#430b00',
    50: '#4b0e00',
    100: '#5f1400',
    200: '#822000',
    300: '#a22d00',
    400: '#c13e00',
    500: '#dc5600',
    600: '#f27400',
    700: '#ff9926',
    800: '#ffc46b',
    900: '#ffeea4',
    1000: '#fffcef',
  },
  red: {
    0: '#35021a',
    25: '#420320',
    33: '#450322',
    50: '#4d0425',
    100: '#61092d',
    200: '#851338',
    300: '#a81d40',
    400: '#cc2748',
    500: '#f23154',
    600: '#f46b7d',
    700: '#f796a3',
    800: '#fabfc9',
    900: '#fee9ed',
    1000: '#fffbfc',
  },
  slate: {
    0: '#14171d',
    25: '#1b1e25',
    33: '#1d2027',
    50: '#21252c',
    100: '#2b3039',
    200: '#3e444f',
    300: '#505865',
    400: '#636c7b',
    500: '#768193',
    600: '#8c99ad',
    700: '#a9b2c2',
    800: '#c9ced8',
    900: '#eceef1',
    1000: '#fcfcfc',
  },
  blurple: {
    0: '#1e0a3f',
    25: '#270e50',
    33: '#2a0f55',
    50: '#2f105f',
    100: '#3c147c',
    200: '#5017b7',
    300: '#581bf7',
    400: '#5f4cfe',
    500: '#746cff',
    600: '#9289fe',
    700: '#b1a7fd',
    800: '#d1c8fc',
    900: '#efecfd',
    1000: '#fcfcff',
  },
};

const elevationColors = {
  0: '#14171d',
  20: '#1A1D24',
  40: '#1F222A',
  60: '#23272F',
  80: '#272C34',
  100: '#2b3039',
};

// TODO: should be using dark-mode Blue 600
const darkModeFocus = '0 0 0 4px rgba(43, 157, 246, .36)';

const largeShadow =
  '0px 5px 15px 0px rgba(0, 0, 0, 0.12), 0px 15px 35px 0px rgba(14, 14, 17, 0.40)';

const darkModeOverrideMap = {
  color: {
    hue: {
      ...darkModeColors,
    },
    text: {
      onAction: {
        primary: {
          $default: color.neutral[900],
          hovered: color.neutral[900],
          pressed: color.primary[800],
          focused: color.neutral[900],
          disabled: color.neutral[900],
        },
        secondary: {
          $default: color.text,
          hovered: color.text,
          pressed: color.text.subdued,
          focused: color.text,
          disabled: color.text,
        },
        destructive: {
          $default: color.neutral[900],
          hovered: color.neutral[900],
          pressed: color.feedback.critical[800],
          focused: color.neutral[900],
          disabled: color.neutral[900],
        },
      },
      onFeedback: {
        neutral: {
          emphasized: color.neutral[900],
        },
        info: {
          emphasized: color.neutral[900],
        },
        success: {
          emphasized: color.neutral[900],
        },
        attention: {
          emphasized: color.neutral[900],
        },
        critical: {
          emphasized: color.neutral[900],
        },
      },
    },
    icon: {
      onAction: {
        primary: {
          $default: color.neutral[900],
          hovered: color.neutral[900],
          pressed: color.primary[800],
          focused: color.neutral[900],
          disabled: color.neutral[900],
        },
        secondary: {
          $default: color.text,
          hovered: color.text,
          pressed: color.text.subdued,
          focused: color.text,
          disabled: color.text,
        },
        destructive: {
          $default: color.neutral[900],
          hovered: color.neutral[900],
          pressed: color.feedback.critical[800],
          focused: color.neutral[900],
          disabled: color.neutral[900],
        },
      },
      onForm: {
        accent: color.neutral[900],
      },
      feedback: {
        info: {
          $default: color.feedback.info[600],
          subdued: color.feedback.info[600],
          emphasized: color.feedback.info[700],
        },
        success: {
          $default: color.feedback.success[600],
          subdued: color.feedback.success[600],
          emphasized: color.feedback.success[700],
        },
        attention: {
          $default: color.feedback.attention[600],
          subdued: color.feedback.attention[600],
          emphasized: color.feedback.attention[700],
        },
        critical: {
          $default: color.feedback.critical[600],
          subdued: color.feedback.critical[600],
          emphasized: color.feedback.critical[700],
        },
      },
      onFeedback: {
        neutral: {
          $default: color.neutral[600],
          subdued: color.neutral[600],
          emphasized: color.neutral[900],
        },
        info: {
          $default: color.feedback.info[600],
          subdued: color.feedback.info[600],
          emphasized: color.neutral[900],
        },
        success: {
          $default: color.feedback.success[600],
          subdued: color.feedback.success[600],
          emphasized: color.neutral[900],
        },
        attention: {
          $default: color.feedback.attention[600],
          subdued: color.feedback.attention[600],
          emphasized: color.neutral[900],
        },
        critical: {
          $default: color.feedback.critical[600],
          subdued: color.feedback.critical[600],
          emphasized: color.neutral[900],
        },
      },
    },
    border: {
      action: {
        primary: {
          $default: color.primary[400],
          hovered: color.primary[600],
          pressed: color.primary[300],
          focused: color.primary[400],
          disabled: color.primary[400],
        },
        secondary: {
          $default: elevationColors[80],
          hovered: color.neutral[200],
          pressed: elevationColors[100],
          focused: elevationColors[80],
          disabled: elevationColors[80],
        },
        destructive: {
          $default: color.feedback.critical[400],
          hovered: color.feedback.critical[600],
          pressed: color.feedback.critical[300],
          focused: color.feedback.critical[400],
          disabled: color.feedback.critical[400],
        },
      },
      form: {
        accent: createColorStates(
          color.primary as Token.Map<ColorSpectrum>,
          400 as Token.Key<ColorSpectrum>,
        ),
      },
      feedback: {
        critical: {
          emphasized: color.feedback.critical[400],
        },
      },
    },
    background: {
      backdrop: 'rgba(14, 14, 17, 0.70)',
      action: {
        primary: {
          $default: color.primary[400],
          hovered: color.primary[400],
          pressed: color.primary[300],
          focused: color.primary[400],
          disabled: color.primary[400],
        },
        secondary: {
          $default: elevationColors[20],
          hovered: elevationColors[40],
          pressed: elevationColors[20],
          focused: elevationColors[20],
          disabled: elevationColors[20],
        },
        destructive: {
          $default: color.feedback.critical[400],
          hovered: color.feedback.critical[400],
          pressed: color.feedback.critical[300],
          focused: color.feedback.critical[400],
          disabled: color.feedback.critical[400],
        },
      },
      form: {
        accent: createColorStates(
          color.primary as Token.Map<ColorSpectrum>,
          400 as Token.Key<ColorSpectrum>,
        ),
      },
      onForm: {
        accent: color.neutral[900],
      },
      feedback: {
        neutral: {
          $default: color.neutral[25],
        },
        info: {
          $default: color.feedback.info[25],
        },
        success: {
          $default: color.feedback.success[25],
        },
        attention: {
          $default: color.feedback.attention[25],
        },
        critical: {
          $default: color.feedback.critical[25],
          emphasized: color.feedback.critical[400],
        },
      },
    },
  },
  focus: darkModeFocus,
  shadow: {
    small:
      '0px 1px 1px 0px rgba(0, 0, 0, 0.12), 0px 2px 5px 0px rgba(14, 14, 17, 0.40)',
    medium:
      '0px 3px 6px 0px rgba(0, 0, 0, 0.12), 0px 7px 14px 0px rgba(48, 49, 61, 0.08)',
    large: largeShadow,
    xlarge:
      '0px 5px 15px 0px rgba(0, 0, 0, 0.12), 0px 15px 35px 0px rgba(14, 14, 17, 0.40), 0px 50px 100px 0px rgba(14, 14, 17, 0.40)',
    overlay: {
      $default: largeShadow,
      inverted:
        '0px 0px 15px 0px rgba(0, 0, 0, 0.12), 0px 0px 35px 0px rgba(14, 14, 17, 0.40)',
    },
    action: {
      primary: {
        // Brand 500
        $default: '0px -1px 0px 0px #746CFF',
        pressed: '0px -1px 0px 0px transparent',
        hovered: '0px -1px 0px 0px transparent',
        focused: '0px -1px 0px 0px #746CFF',
        disabled: '0px -1px 0px 0px #746CFF',
      },
      secondary: {
        // Neutral 200
        $default: '0px -1px 0px 0px #3E444F',
        pressed: '0px -1px 0px 0px transparent',
        hovered: '0px -1px 0px 0px transparent',
        focused: '0px -1px 0px 0px #3E444F',
        disabled: '0px -1px 0px 0px #3E444F',
      },
      destructive: {
        // Critical 600
        $default: '0px -1px 0px 0px #f46b7d',
        pressed: '0px -1px 0px 0px transparent',
        hovered: '0px -1px 0px 0px transparent',
        focused: '0px -1px 0px 0px #f46b7d',
        disabled: '0px -1px 0px 0px #f46b7d',
      },
    },
  },
};

// [TODO]: once we control dark mode through CSS these overrides should
// be moved into the actual component CSS instead of being customize calls
// we should also remove the elevation overrides here
export const darkModeComponentOverrides = toIntent([
  DialogConfig.customize({
    css: {
      border: `1px solid ${color.border}`,
      topShadow: 'action.secondary',
    },
    uses: [
      assignTokens(tokens, {
        color: {
          background: {
            surface: elevationColors[20],
            offset: elevationColors[60],
          },
        },
      }),
    ],
  }),
  DrawerConfig.customize({
    uses: [
      assignTokens(tokens, {
        color: {
          background: {
            surface: elevationColors[20],
            offset: elevationColors[60],
          },
        },
      }),
    ],
  }),
  TooltipConfig.customize({
    css: {
      backgroundColor: color.neutral[100],
      textColor: 'emphasized',
      topShadow: 'action.secondary',
      border: 'none',
      borderTop: `1px solid ${color.neutral[200]}`,
      borderColor: color.neutral[200],
    },
  }),
  BackdropConfig.customize({
    css: {
      backgroundColor: 'backdrop',
    },
  }),
  PopoverConfig.customize({
    uses: [
      assignTokens(tokens, {
        color: {
          background: {
            surface: elevationColors[40],
            offset: elevationColors[100],
          },
        },
      }),
    ],
  }),
  CounterConfig.customize({
    variants: {
      type: {
        default: css({
          backgroundColor: tokens.color.neutral[100],
        }),
        emphasized: css({
          textColor: 'emphasized',
        }),
      },
    },
  }),
  LinkConfig.customize({
    uses: [
      assignTokens(tokens, {
        color: {
          hue: {
            blurple: {
              600: darkModeColors.blurple[600],
            },
          },
        },
      }),
    ],
  }),
]);

/**
 * This intent conditionally overrides color tokens to their dark mode variants
 * when the user agent requests the page be rendered with a dark color scheme.
 *
 * @note You might want `darkModeWebContent`, which also overrides CSS properties.
 */
export const darkMode = (): Token.OverrideIntent =>
  assignConditionalTokens(
    '@media (prefers-color-scheme: dark)',
    tokens,
    darkModeOverrideMap,
  );

/**
 * This intent conditionally overrides color tokens, and CSS properties
 * to their dark mode variants when the user agent requests the page be rendered
 * with a dark color scheme.
 */
export const darkModeWebContent = (): Intent =>
  toIntent([
    darkMode(),
    // TODO: This should include darkModeComponentOverrides, but we can't conditionally
    // apply that intent based on a media query. To do this, we need a new Engine
    // API like `assignConditionalIntents`.
    css({
      '@media (prefers-color-scheme: dark)': {colorScheme: 'dark'},
    }),
  ]);

/**
 * This intent unconditionally overrides color tokens to their dark mode variants.
 * This will turn the entire component subtree to dark mode regardless of the user
 * agent's color mode preference.
 *
 * @note You might want `darkModeWebContentOverrides`, which also overrides CSS properties.
 */
export const darkModeTokenOverrides = (): Token.OverrideIntent =>
  assignTokens(tokens, darkModeOverrideMap);

/**
 * This intent unconditionally overrides color tokens, components, and CSS properties
 * to their dark mode variants. This will turn the entire component subtree to dark
 * mode regardless of the user agent's color mode preference.
 */
export const darkModeWebContentOverrides = (): Intent =>
  toIntent([
    darkModeTokenOverrides(),
    darkModeComponentOverrides,
    css({colorScheme: 'dark'}),
  ]);

/**
 * This intent conditionally overrides color tokens, components, and CSS properties
 * between light and dark mode based on the specified "mode". This will switch the entire
 * component subtree to the requested mode without remounting the whole component tree.
 */
export const themeOverrides = (mode: 'light' | 'dark'): Intent => {
  if (mode === 'dark') {
    return darkModeWebContentOverrides();
  }

  return toIntent([
    lightModeTokenOverrides(),
    lightModeComponentOverrides,
    css({colorScheme: 'light'}),
  ]);
};

/**
 * The dark mode theme, exported from `@sail/ui/themes` as `darkMode`.  The
 * individual intents above stay re-exported from `@sail/ui` for backwards
 * compatibility.
 */
export const darkModeTheme = createTheme(
  {
    name: 'Dark Mode',
    description: 'The official Sail UI dark mode.',
    category: 'Foundations',
  },
  [
    darkModeTokenOverrides(),
    darkModeComponentOverrides,
    css({colorScheme: 'dark'}),
  ],
);
